import React, { useState, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Star, MapPin, Heart, ArrowLeft } from "lucide-react";
import FilterBar from "../../components/shared/FilterBar";
import ServiceCardSkeleton from "../../components/shared/ServiceCardSkeleton";
import { useCategoryServices } from "../../hooks/useCategoryServices";
import { useServiceCategory } from "../../services/serviceCategoryService";
import useUserLocation from "../../hooks/useUserLocation";

const CategoryServices = () => {
  const { category } = useParams(); // This is the category ID (UUID)
  const [searchParams] = useSearchParams();
  
  // Auto-detect and use user location
  const { userLocation } = useUserLocation();
  
  // Fetch category details
  const { data: categoryResponse, isLoading: categoryLoading } = useServiceCategory(category);
  const categoryData = categoryResponse?.data || categoryResponse;
  const categoryTitle = searchParams.get("title") || categoryData?.category_name || category?.replace(/-/g, " ");

  // Fetch services for this category with location-based filtering
  // Use distance sorting when location is available, otherwise use created_at
  const { services: allServices, isLoading: servicesLoading, error } = useCategoryServices(category, {
    per_page: 100, // Get more services for filtering
    sort_by: userLocation?.latitude && userLocation?.longitude ? 'distance' : 'created_at',
    sort_order: userLocation?.latitude && userLocation?.longitude ? 'asc' : 'desc',
    location: userLocation // Pass user location for sorting (not filtering)
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPriceRange, setSelectedPriceRange] = useState("all");
  const [selectedRating, setSelectedRating] = useState("all");
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [hoveredService, setHoveredService] = useState(null);

  // Services are already transformed by the hook, just use them directly
  const transformedServices = allServices;

  // Apply filters
  const filteredServices = useMemo(() => {
    let filtered = [...transformedServices];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter((service) =>
        service.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Price range filter
    if (selectedPriceRange !== "all") {
      const [min, max] = selectedPriceRange.split("-").map(Number);
      filtered = filtered.filter((service) => {
        const price = service.price || 0;
        if (max) {
          return price >= min && price <= max;
        }
        return price >= min;
      });
    }

    // Rating filter
    if (selectedRating !== "all") {
      const minRating = parseFloat(selectedRating.replace("+", ""));
      filtered = filtered.filter((service) => (service.rating || 0) >= minRating);
    }

    return filtered;
  }, [transformedServices, searchTerm, selectedPriceRange, selectedRating]);

  const isLoading = categoryLoading || servicesLoading;

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile: Title Section */}
      <div className="bg-white border-b border-gray-200 md:hidden">
        <div className="page-container py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-black transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="text-sm">Back</span>
            </Link>
            <h1 className="text-lg font-bold text-gray-900 capitalize text-center flex-1 mx-4">
              {categoryTitle || 'Browse by Category'}
            </h1>
            <div className="w-12"></div>
          </div>
        </div>
      </div>

      {/* Mobile: Sticky Filter Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm md:hidden">
        <div className="page-container py-3">
          <FilterBar
            selectedPriceRange={selectedPriceRange}
            setSelectedPriceRange={setSelectedPriceRange}
            selectedRating={selectedRating}
            setSelectedRating={setSelectedRating}
            showMoreFilters={showMoreFilters}
            setShowMoreFilters={setShowMoreFilters}
            showCategoryFilter={false}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        </div>
      </div>

      {/* Desktop: One Line Header - Back | Filters | Count (Sticky) */}
      <div className="hidden md:block bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="page-container py-4">
          <div className="flex items-center justify-between gap-6">
            {/* Back Button */}
            <Link
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-black transition-colors whitespace-nowrap"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="text-base">Back</span>
            </Link>

            <div className="flex-1"></div>

            {/* Filter Bar on Right */}
            <div className="flex items-center gap-4">
              <FilterBar
                selectedPriceRange={selectedPriceRange}
                setSelectedPriceRange={setSelectedPriceRange}
                selectedRating={selectedRating}
                setSelectedRating={setSelectedRating}
                showMoreFilters={showMoreFilters}
                setShowMoreFilters={setShowMoreFilters}
                showCategoryFilter={false}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />

              {/* Results Count */}
              <p className="text-sm text-gray-600 whitespace-nowrap">
                {isLoading ? "Loading..." : `${filteredServices.length} services`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="page-container py-4 md:py-8 md:px-8  px-8">
        {/* Mobile Results Count */}
        <p className="text-sm text-gray-600 mb-4 md:hidden">
          {isLoading ? "Loading..." : `${filteredServices.length} services found`}
        </p>

        {/* Desktop: Title next to cards */}
        <div className="hidden md:block mb-6">
          <p className="text-sm text-gray-600 mb-1">Browse by Category</p>
          <h2 className="text-2xl font-bold text-gray-900 capitalize">
            {categoryTitle || 'Services'}
          </h2>
        </div>

        <div className="flex gap-8 relative">
          {/* Services Grid */}
          <div className="flex-1">
            {isLoading ? (
              <ServiceCardSkeleton count={8} />
            ) : error ? (
              <div className="flex justify-center items-center py-20">
                <div className="text-center">
                  <div className="text-red-500 text-lg font-medium mb-2">Failed to load services</div>
                  <p className="text-gray-600">Please try again later</p>
                </div>
              </div>
            ) : filteredServices.length === 0 ? (
              <div className="flex justify-center items-center py-20">
                <div className="text-center">
                  <div className="text-gray-500 text-lg font-medium mb-2">No services found</div>
                  <p className="text-gray-600">Try adjusting your filters</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-4">
                {filteredServices.map((service) => (
                <div
                  key={service.id}
                  onMouseEnter={() => setHoveredService(service)}
                  onMouseLeave={() => setHoveredService(null)}
                  className="relative"
                >
                  <Link
                    to={`/services/${service.id}`}
                    className="block group bg-white rounded-xl md:rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-200 md:border-transparent md:hover:border-gray-200"
                  >
                    {/* Image */}
                    <div className="relative h-52 md:h-52 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />

                      {/* Featured Badge */}
                      {service.isFeatured && (
                        <div className="absolute top-2 md:top-3 left-2 md:left-3 bg-black text-white px-2 md:px-3 py-1 rounded-full text-xs font-medium">
                          Featured
                        </div>
                      )}

                      {/* Heart Icon */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className="absolute top-2 md:top-3 right-2 md:right-3 p-1.5 md:p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors md:opacity-0 md:group-hover:opacity-100"
                        aria-label="Add to favorites"
                      >
                        <Heart className="w-4 h-4 text-gray-700" />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="p-3 md:p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-sm md:text-sm font-semibold text-gray-900 line-clamp-2 flex-1">
                          {service.title}
                        </h3>
                        <div className="flex items-center ml-2 flex-shrink-0">
                          <Star className="w-3 h-3 fill-current text-gray-900" />
                          <span className="text-xs font-medium text-gray-900 ml-1">
                            {service.rating > 0 ? Number(service.rating).toFixed(2) : 'New'}
                          </span>
                        </div>
                      </div>

                      <p className="text-xs text-gray-500 mb-1">
                        {service.vendor}
                      </p>

                      <div className="flex items-center text-xs text-gray-500 mb-2 md:mb-3">
                        <MapPin className="w-3 h-3 mr-1" />
                        {service.location}
                      </div>

                      <div className="flex items-center justify-between pt-2 md:pt-3 border-t border-gray-100">
                        <div>
                          <span className="text-sm font-bold text-gray-900">
                            ${service.price > 0 ? service.price.toFixed(0) : 'Contact'}
                          </span>
                          <span className="text-xs text-gray-600 ml-1">
                            starting from
                          </span>
                        </div>
                        {service.reviewCount > 0 && (
                          <span className="text-xs text-gray-500 hidden sm:inline">
                            {service.reviewCount} reviews
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
                ))}
              </div>
            )}
          </div>

          {/* Side Detail Panel - Shows on Hover (Desktop Only) */}
          {hoveredService && (
            <div className="hidden xl:block fixed right-8 top-1/4 w-96 z-30">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 animate-fade-in">
                <div className="relative h-48 rounded-xl overflow-hidden mb-4">
                  <img
                    src={hoveredService.image}
                    alt={hoveredService.title}
                    className="w-full h-full object-cover"
                  />
                  {hoveredService.isFeatured && (
                    <div className="absolute top-3 left-3 bg-black text-white px-3 py-1 rounded-full text-xs font-medium">
                      Featured
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {hoveredService.title}
                </h3>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center">
                    <Star className="w-4 h-4 fill-current text-gray-900 mr-1" />
                    <span className="font-semibold">
                      {hoveredService.rating > 0 ? Number(hoveredService.rating).toFixed(2) : 'New'}
                    </span>
                    {hoveredService.reviewCount > 0 && (
                      <span className="text-gray-500 text-sm ml-1">
                        ({hoveredService.reviewCount} reviews)
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center text-gray-600 mb-4">
                  <MapPin className="w-4 h-4 mr-2" />
                  {hoveredService.location}
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Provided by:</p>
                  <p className="font-semibold text-gray-900">
                    {hoveredService.vendor}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-baseline justify-between mb-4">
                    <div>
                      <span className="text-2xl font-bold text-gray-900">
                        ${hoveredService.price > 0 ? hoveredService.price.toFixed(0) : 'Contact'}
                      </span>
                      {hoveredService.price > 0 && (
                        <span className="text-gray-600 text-sm ml-2">
                          per event
                        </span>
                      )}
                    </div>
                  </div>

                  <Link
                    to={`/services/${hoveredService.id}`}
                    className="block w-full py-3 bg-black text-white text-center rounded-xl font-light hover:bg-gray-800 transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryServices;
